import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { deleteApi } from '../../api/deletes'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useDeleteWizard } from '../../delete/DeleteWizardContext'
import { useAsync } from '../../hooks/useAsync'
import { formatInt } from '../../lib/format'
import { DELETE_STEPS } from './steps'

/* Step 2, combining "batchbooks_delete_filter_records_synced" and
   "batchbooks_delete_paste_reference_numbers_updated".

   The Filter Records tab now mirrors the Export/Modify filter layout: vertical
   form with a reference field, preset/custom date radio buttons, and a live
   matching-record count from QuickBooks. The Paste Reference Numbers tab keeps
   its own layout since that workflow is unique to Delete. */

/** Human label for the reference field the backend filters on. */
function referenceLabel(field: string): string {
  switch (field) {
    case 'DisplayName':
      return 'Name'
    case 'DocNumber':
      return 'Doc Number'
    default:
      return field
  }
}

/** The date presets shown in Export/Modify style. */
const DATE_PRESETS = [
  'This fiscal year',
  'Last fiscal year',
  'This quarter',
  'Last quarter',
  'This month',
  'Last month',
]

export function DeleteFilterPage() {
  const wizard = useDeleteWizard()
  const navigate = useNavigate()
  const { current } = useCompanies()
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filters are meaningless without a subject, and the wizard state is in
  // memory only — a reload or deep link arrives with nothing set.
  if (!wizard.entityType) return <Navigate to="/app/delete" replace />

  const subject = wizard.entity?.label ?? wizard.entityType
  const refs = wizard.source === 'refs'
  const lineCount = Math.max(wizard.refs.split('\n').length, 5)
  const review = wizard.review ?? []

  // --- Filter tab: date mode derived from wizard.dateRange ---
  const custom = wizard.dateRange === 'Custom'
  const refId = `del-ref-${wizard.entityType}`

  // Live count from QuickBooks (filter tab only, mirrors Export/Modify)
  const preview = useAsync(
    () => {
      if (refs) return Promise.resolve(null)
      const companyId = current?.id
      if (!companyId || !wizard.entityType) return Promise.resolve(null)
      return deleteApi.search({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        source: 'filter',
        dateRange: custom ? 'Custom' : wizard.dateRange,
        from: custom ? wizard.from || undefined : undefined,
        to: custom ? wizard.to || undefined : undefined,
        amountMin: wizard.amountMin || undefined,
        amountMax: wizard.amountMax || undefined,
        limit: 100,
      })
    },
    [
      refs,
      current?.id,
      wizard.entityType,
      wizard.dateRange,
      wizard.from,
      wizard.to,
      wizard.amountMin,
      wizard.amountMax,
    ],
  )

  // Update review and exportColumns when auto-preview returns
  const previewItems = preview.data?.items ?? null
  if (!refs && previewItems && previewItems !== review) {
    wizard.setReview(previewItems)
    wizard.setExportColumns(preview.data?.exportColumns ?? null)
  }

  const searchByRefs = async () => {
    const companyId = current?.id
    if (!companyId) {
      setError('No QuickBooks company is connected.')
      return
    }
    setSearching(true)
    setError(null)
    try {
      const result = await deleteApi.search({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        source: 'refs',
        reference: wizard.refs || undefined,
        limit: 100,
      })
      wizard.setReview(result.items)
      wizard.setExportColumns(result.exportColumns ?? null)
    } catch (cause: unknown) {
      wizard.setReview(null)
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not search QuickBooks for these records.',
      )
    } finally {
      setSearching(false)
    }
  }

  // --- Date helpers for the Export-style layout ---
  const setDatePreset = (preset: string) => {
    wizard.setDateRange(preset)
  }
  const setDateCustom = () => {
    wizard.setDateRange('Custom')
  }
  const isPreset = !custom

  const count = refs
    ? review.length
    : preview.data?.total ?? null

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={DELETE_STEPS} current={1} />

      <h2>Filter Records to Delete</h2>
      <p className="bb-map-help">Define the criteria for the {subject.toLowerCase()} you wish to permanently remove.</p>

      <div className="bb-tabs" role="tablist" aria-label="How to find the records">
        <button
          className={!refs ? 'bb-tab bb-tab-on' : 'bb-tab'}
          type="button"
          role="tab"
          aria-selected={!refs}
          onClick={() => wizard.setSource('filter')}
        >
          Filter Records
        </button>
        <button
          className={refs ? 'bb-tab bb-tab-on' : 'bb-tab'}
          type="button"
          role="tab"
          aria-selected={refs}
          onClick={() => wizard.setSource('refs')}
        >
          Paste Reference Numbers
        </button>
      </div>

      {refs ? (
        <div className="bb-paste">
          <div>
            <h3 className="bb-sub-h">Paste Reference Numbers</h3>
            <p className="bb-map-help" style={{ marginBottom: 16 }}>
              Enter the {subject} numbers you wish to permanently delete from QuickBooks Online.
            </p>

            <div className="bb-lines">
              <div className="bb-lines-n" aria-hidden="true">
                {Array.from({ length: lineCount }, (_, i) => (
                  <div key={i}>{i + 1}</div>
                ))}
              </div>
              <textarea
                className="bb-lines-t"
                aria-label={`${subject} reference numbers, one per line`}
                spellCheck={false}
                value={wizard.refs}
                onChange={(event) => wizard.setRefs(event.target.value)}
              />
            </div>

            <div className="bb-lines-meta">
              <span>{formatInt(wizard.refList.length)} numbers detected</span>
              <button
                className="bb-clear"
                type="button"
                disabled={!wizard.refs}
                onClick={() => wizard.setRefs('')}
              >
                Clear all
              </button>
              <span className="spacer" />
              <button
                className="bb-btn bb-btn-navy"
                type="button"
                disabled={searching || wizard.refList.length === 0}
                onClick={() => void searchByRefs()}
              >
                {searching ? 'Searching…' : 'Find Records'}
              </button>
            </div>
          </div>

          <aside>
            <div className="bb-rules">
              <h3 className="bb-rules-h">
                <BbIcon name="info" />
                Formatting rules
              </h3>
              <ul>
                <li className="bb-rule-i bb-rule-yes">
                  <BbIcon name="check-circle" />
                  <span>
                    Enter <b>one reference number per line</b>.
                  </span>
                </li>
                <li className="bb-rule-i bb-rule-yes">
                  <BbIcon name="check-circle" />
                  <span>Exact match is required (case-sensitive based on QBO settings).</span>
                </li>
                <li className="bb-rule-i bb-rule-no">
                  <BbIcon name="x-circle" />
                  <span>Do not include commas, spaces, or other delimiters.</span>
                </li>
              </ul>

              <div className="bb-note">
                <div className="bb-note-h">
                  <BbIcon name="warning" />
                  High-stakes action
                </div>
                <p>
                  Deletion cannot be undone. You will have a chance to review all found records in
                  the next step before confirming deletion.
                </p>
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <div className="bb-filters">
          <div className="bb-filter-row">
            <label className="bb-filter-k" htmlFor={refId}>
              {referenceLabel(preview.data?.items?.[0]?.docNumber != null ? 'DocNumber' : 'DisplayName')} :
            </label>
            <input
              className="bb-input"
              id={refId}
              value=""
              placeholder={referenceLabel('Reference')}
              disabled
              style={{ opacity: 0.5 }}
            />
          </div>

          <h3 className="bb-sub-h">Date</h3>

          <div className="bb-filter-row">
            <label className="bb-radio">
              <input
                type="radio"
                name="del_date_mode"
                value="preset"
                checked={isPreset}
                onChange={() => setDatePreset(DATE_PRESETS[0])}
              />
              Preset Date :
            </label>
            <select
              className="bb-select"
              aria-label="Preset date range"
              disabled={custom}
              value={isPreset ? wizard.dateRange : DATE_PRESETS[0]}
              onChange={(event) => setDatePreset(event.target.value)}
            >
              {DATE_PRESETS.map((preset) => (
                <option value={preset} key={preset}>
                  {preset}
                </option>
              ))}
            </select>
          </div>

          <div className="bb-filter-row">
            <label className="bb-radio">
              <input
                type="radio"
                name="del_date_mode"
                value="custom"
                checked={custom}
                onChange={setDateCustom}
              />
              Customized Date :
            </label>
            <span className="bb-dates">
              <input
                className="bb-input"
                type="date"
                aria-label="Start date"
                disabled={!custom}
                value={wizard.from}
                onChange={(event) => wizard.setFrom(event.target.value)}
              />
              <button
                className="bb-clear"
                type="button"
                disabled={!custom || !wizard.from}
                onClick={() => wizard.setFrom('')}
              >
                clear
              </button>
              <input
                className="bb-input"
                type="date"
                aria-label="End date"
                disabled={!custom}
                value={wizard.to}
                onChange={(event) => wizard.setTo(event.target.value)}
              />
              <button
                className="bb-clear"
                type="button"
                disabled={!custom || !wizard.to}
                onClick={() => wizard.setTo('')}
              >
                clear
              </button>
            </span>
          </div>

          <div className="bb-filter-row bb-filter-count">
            {preview.loading ? (
              <span className="bb-map-help">Checking QuickBooks…</span>
            ) : preview.error ? (
              <span className="bb-map-help" style={{ color: 'var(--bb-red)' }}>
                Could not reach QuickBooks for a count.
              </span>
            ) : count == null ? (
              <span className="bb-map-help">Adjust the filters above to find records.</span>
            ) : (
              <span className="bb-map-help">
                {formatInt(count)} matching record{count === 1 ? '' : 's'} in QuickBooks
              </span>
            )}
          </div>
        </div>
      )}

      {error ? (
        <Alert tone="err" title="Could not search QuickBooks" style={{ marginTop: 16 }}>
          {error}
        </Alert>
      ) : null}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/delete">
          <BbIcon name="arrow-left" />
          Back
        </Link>
        <span className="bb-wizard-foot-r">
          <button
            className="bb-btn bb-btn-danger"
            type="button"
            disabled={review.length === 0 || searching || preview.loading}
            onClick={() => {
              wizard.setReview(review)
              navigate('/app/delete/review')
            }}
          >
            Review for Deletion ({formatInt(review.length)})
            <BbIcon name="arrow-right" />
          </button>
        </span>
      </div>
    </div>
  )
}
